import { SITE_ORIGIN } from "./seoMeta";
import { sanitizeSharePathname } from "./villageSlug";
import { expandShortPath } from "./shortShareUrl";

export const DEFAULT_SHARE_TEXT = "Guarda questa cartolina in diretta su 6AMICO!";

/** Alias: origine sito / URL assoluti. */
export function siteOrigin() {
  return SITE_ORIGIN;
}

/**
 * Absolute share URL — long canonical path (Facebook blue-links these).
 * Strips paste junk: "/comune/assisi unione…" → "/comune/assisi".
 */
export function toAbsoluteShareUrl(urlPost) {
  if (!urlPost) {
    if (typeof window !== "undefined") {
      const path = sanitizeSharePathname(window.location.pathname);
      const expanded = expandShortPath(path) || path;
      return `${SITE_ORIGIN}${expanded}${window.location.search || ""}`;
    }
    return `${SITE_ORIGIN}/`;
  }
  let s = String(urlPost).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("//")) {
    const abs = s.startsWith("//") ? `https:${s}` : s;
    try {
      const u = new URL(abs.split(/\s+/)[0].replace(/[.,;:!?)\]}>]+$/g, ""));
      let path = sanitizeSharePathname(u.pathname);
      path = expandShortPath(path) || path;
      return `${SITE_ORIGIN}${path}${u.search || ""}`;
    } catch {
      return `${SITE_ORIGIN}/`;
    }
  }
  s = s.split(/\s+/)[0].replace(/[.,;:!?)\]}>]+$/g, "");
  let path = sanitizeSharePathname(s.startsWith("/") ? s : `/${s}`);
  path = expandShortPath(path) || path;
  return `${SITE_ORIGIN}${path}`;
}

export function toAbsoluteUrl(urlOrPath) {
  return toAbsoluteShareUrl(urlOrPath);
}

export function facebookSharerUrl(url) {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(toAbsoluteShareUrl(url))}`;
}

export function whatsappShareUrl(text, url) {
  const cleanUrl = toAbsoluteShareUrl(url);
  const caption = String(text || DEFAULT_SHARE_TEXT).replace(/https?:\/\/\S+/gi, "").trim();
  const full = caption ? `${caption}\n${cleanUrl}` : cleanUrl;
  return `https://wa.me/?text=${encodeURIComponent(full)}`;
}

export function canUseNativeShare() {
  if (typeof navigator === "undefined" || !navigator.share) return false;
  if (navigator.userAgentData?.mobile === true) return true;
  if (navigator.userAgentData?.mobile === false) {
    const p = String(navigator.userAgentData.platform || "");
    if (/Windows/i.test(p)) return false;
  }
  const ua = navigator.userAgent || "";
  const plat = navigator.platform || "";
  if (/Windows/i.test(plat) && !/Mobile|Android|iPhone|iPad|Touch/i.test(ua)) return false;
  return true;
}

export function preferClipboardOverNativeShare() {
  return !canUseNativeShare();
}

function removeChooser(root) {
  if (root && root.parentNode) root.parentNode.removeChild(root);
}

function stripUrlsFromText(text) {
  return String(text || "")
    .replace(/https?:\/\/\S+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function showDesktopShareChooser({ title, text, url }) {
  return new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve({ ok: false });
      return;
    }

    const cleanUrl = toAbsoluteShareUrl(url);
    const fb = facebookSharerUrl(cleanUrl);
    const wa = whatsappShareUrl(text, cleanUrl);

    const root = document.createElement("div");
    root.setAttribute("data-testid", "condividi-desktop-chooser");
    root.style.cssText =
      "position:fixed;inset:0;z-index:10000;background:rgba(15,23,42,0.55);display:flex;align-items:center;justify-content:center;padding:16px;";

    const panel = document.createElement("div");
    panel.style.cssText =
      "background:#fff;border-radius:16px;max-width:320px;width:100%;padding:20px;box-shadow:0 20px 50px rgba(0,0,0,.25);font-family:system-ui,sans-serif;";
    panel.innerHTML = `
      <p style="margin:0 0 4px;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#b45309;">Condividi · 6amico</p>
      <h3 style="margin:0 0 8px;font-size:18px;color:#1a1a1a;">${(title || "6amico").replace(/</g, "&lt;")}</h3>
      <p style="margin:0 0 8px;font-size:12px;color:#1877F2;word-break:break-all;">${cleanUrl.replace(/</g, "&lt;")}</p>
      <div style="display:flex;flex-direction:column;gap:8px;">
        <a data-share="fb" href="${fb}" target="_blank" rel="noopener noreferrer"
           style="display:block;text-align:center;padding:12px;border-radius:999px;background:#1877F2;color:#fff;text-decoration:none;font-weight:600;font-size:14px;">Facebook · anteprima</a>
        <a data-share="wa" href="${wa}" target="_blank" rel="noopener noreferrer"
           style="display:block;text-align:center;padding:12px;border-radius:999px;background:#25D366;color:#fff;text-decoration:none;font-weight:600;font-size:14px;">WhatsApp</a>
        <button type="button" data-share="close"
           style="margin-top:4px;padding:10px;border:none;background:transparent;color:#6b7280;cursor:pointer;font-size:13px;">Annulla</button>
      </div>
    `;

    const finish = (result) => {
      removeChooser(root);
      document.removeEventListener("keydown", onKey);
      resolve(result);
    };
    const onKey = (e) => {
      if (e.key === "Escape") finish({ ok: false, aborted: true });
    };

    root.addEventListener("click", (e) => {
      if (e.target === root) finish({ ok: false, aborted: true });
    });
    panel.querySelector('[data-share="close"]').addEventListener("click", () =>
      finish({ ok: false, aborted: true }),
    );
    panel.querySelector('[data-share="wa"]').addEventListener("click", () =>
      finish({ ok: true, method: "whatsapp" }),
    );
    panel.querySelector('[data-share="fb"]').addEventListener("click", () =>
      finish({ ok: true, method: "facebook" }),
    );

    root.appendChild(panel);
    document.body.appendChild(root);
    document.addEventListener("keydown", onKey);
  });
}

export async function condividiSuSocial(titoloOrOpts, urlPost, options = {}) {
  let title;
  let text;
  let url;

  if (titoloOrOpts && typeof titoloOrOpts === "object" && !Array.isArray(titoloOrOpts)) {
    title = titoloOrOpts.title || "6amico";
    text = stripUrlsFromText(titoloOrOpts.text || DEFAULT_SHARE_TEXT);
    url = toAbsoluteShareUrl(titoloOrOpts.url);
  } else {
    title = titoloOrOpts || "6amico";
    text = stripUrlsFromText(options.text || DEFAULT_SHARE_TEXT);
    url = toAbsoluteShareUrl(urlPost);
  }

  if (canUseNativeShare()) {
    try {
      await navigator.share({ title, text, url });
      return { ok: true, method: "native" };
    } catch (err) {
      if (err && err.name === "AbortError") return { ok: false, aborted: true };
    }
  }

  return showDesktopShareChooser({ title, text, url });
}

export default condividiSuSocial;
